import React, { useEffect, useState } from 'react';
import { useParams } from 'react-router-dom';
import classNames from 'classnames';
import { format } from 'date-fns';
import styled from 'styled-components';

// Components
import FavoriteIcon from '@icons/FavoriteIcon';
import PropertyDetailContacts from './PropertyDetailContacts';
import PropertyInquiryButton from './PropertyInquiryButton';
import Mapbox from '../../_molecules/Mapbox';
import HeroCarouselSlide from '@atoms/HeroCarouselSlide';
import MemberCardRedesign from '@molecules/MemberCardRedesign';
import HeroCarousel from '@molecules/HeroCarousel';
import LoadingArea from '@molecules/LoadingArea';
import {
    BodyLarge,
    BodyMedium,
    BodySmall,
    ButtonOutlineRedesign,
    DividerLightGreyStyled,
    H3,
    H4,
    LabelLarge,
    LabelSmall,
    LinkSmall,
    Ul,
} from '@styled';

// Functionality
import useNARDeepLinks from './useNARDeepLinks';
import useSavedProperties from '../../../hooks/useSavedProperties';
import usePropertyViews from '../../../hooks/usePropertyViews';
import { getDisplayAddress } from '@general/helpers/address.helpers';
import { mediaQueries } from '@general/breakpoints';
import useProperty from '@hooks/useProperty';
import { useScreenSmDown, useScreenMinMd } from '@hooks/useResponsive';
import {
    getDisplayCenter,
    getDisplayDensity,
    getDisplayFacility,
    getDisplayListingStatus,
    getDisplayPurpose,
    getDisplaySize,
} from '@general/entities/property';
// import UniquePropertyViewsList from '@molecules/UniquePropertyViewsDropdown';
import PropertyViewsLink from './PropertyViewsLink';
import { convertUniquePropertyViewsToDropdown } from '@general/helpers/property.helpers';

import getFavoriteHook from '@hooks/getFavoriteHook';
import useAuthorized from '@hooks/useAuthorized';
import useIcscTracking from '@tracking/useIcscTracking';
import useViewer from '@hooks/useViewer';

const Description = styled(BodySmall)`
    p {
        font-size: 14px;
        font-style: normal;
        font-weight: 400;
        line-height: 21px;
        margin: 0;
    }
`;

const MarketplacesContainerStyled = styled.div`
    @media ${mediaQueries.md} {
        display: flex;
    }
`;

const MarketplacesButtonStyled = styled(ButtonOutlineRedesign)`
    display: block;
    margin-top: 8px;
    text-align: center;

    @media ${mediaQueries.md} {
        margin-top: 0;
        min-width: 318px;
        text-align: center;

        &:not(:first-child) {
            margin-left: 1rem;
        }
    }
`;

const PropertyDetail = () => {
    // -----------------------------
    // State
    // -----------------------------

    const { id } = useParams();
    const { isLoggedIn } = useAuthorized();
    const { data: property, refetch } = useProperty({ id });
    const { data: savedProperties } = useSavedProperties();
    const { viewer } = useViewer();
    const { trackEvent } = useIcscTracking();
    const {
        tradeAreaDetailsUrl,
        commercialMapsUrl,
        attributeSiteSelectionUrl,
        commercialPropertyReportUrl,
        commercialTradeAreaReport,
    } = useNARDeepLinks({ property });

    const isScreenSmDown = useScreenSmDown();
    const isScreenMinMd = useScreenMinMd();

    // Property Views
    const { data: propertyViews, uniqueViews } = usePropertyViews(id);
    const [uniqueViewsForDropdown, setUniqueViewsForDropdown] = useState([]);

    // Favoriting
    const [isFavorite, setIsFavorite] = useState(false);
    const useFavorite = getFavoriteHook({ type: 'property', isFavorite });
    const { isLoadingFavorite, saveFavorite, removeFavorite } = useFavorite();

    // -----------------------------
    // Effects
    // -----------------------------

    useEffect(() => {
        if (!property || !savedProperties) {
            return;
        }

        setIsFavorite(savedProperties.map(p => p.recordId).includes(property.recordId));
    }, [property, savedProperties]);

    useEffect(() => {
        if (!uniqueViews || !viewer) {
            return;
        }

        const convertedUniqueViews = convertUniquePropertyViewsToDropdown(uniqueViews, viewer.recordId);

        setUniqueViewsForDropdown(convertedUniqueViews);
    }, [uniqueViews, viewer]);
    // -----------------------------
    // Helpers
    // -----------------------------

    const onFavorite = () => {
        if (isFavorite) {
            removeFavorite(property.recordId).then(() => {
                setIsFavorite(false);
            });
        } else {
            saveFavorite(property.recordId).then(() => {
                setIsFavorite(true);
            });
        }
    };

    const getManagerLocation = () => {
        const preferred = property?.manager.company?.addresses.find(address => address.preferredMailing);

        if (!preferred) {
            return null;
        }

        return getDisplayAddress(preferred);
    };

    const handleMemberCardGenericClick = () => {
        trackEvent({
            click: {
                target: 'click_property_detail_member_card',
                value: 'Click Member Card',
            },
        });
    };

    // -----------------------------
    // Render
    // -----------------------------

    if (!property) {
        return <LoadingArea />;
    }

    const manager = property?.manager;
    const isViewerPropertyManager = viewer?.recordId === property?.manager?.recordId;

    return (
        <>
            <div className="mt-4 mb-1rem d-flex justify-content-between">
                <div>
                    <H3 className="mb-1.5rem">{property.title}</H3>
                    <LabelLarge>{property.location}</LabelLarge>
                    {property.subpremiseId && (
                        <div>
                            <LabelLarge>Suite/Unit No.:</LabelLarge>{' '}
                            <LabelLarge className="font-normal">{property.subpremiseId}</LabelLarge>
                        </div>
                    )}
                </div>

                <div className="d-flex justify-content-between align-items-center">
                    <FavoriteIcon
                        id={id}
                        isLoggedIn={isLoggedIn}
                        isFavorite={isFavorite}
                        isLoading={isLoadingFavorite}
                        tooltipLabel="Property"
                        onClick={onFavorite}
                    />
                </div>
            </div>

            <div className="row">
                <div className="col-lg-6">
                    {property.updatedAt && (
                        <LabelSmall className="d-inline-block text-black">
                            Last Updated {format(new Date(property.updatedAt), 'MMMM d, yyyy')}
                        </LabelSmall>
                    )}
                    {propertyViews?.length > 0 && (
                        <div>
                            <LabelSmall className="d-inline-block text-black">
                                Total views: {propertyViews.length}
                            </LabelSmall>
                        </div>
                    )}
                    {isViewerPropertyManager && uniqueViews?.length > 0 && (
                        <PropertyViewsLink uniqueViews={uniqueViews} />
                    )}
                    <Ul className="mt-1rem">
                        {property.listingStatus && (
                            <li>
                                <BodyMedium>
                                    <strong>Listing Status</strong>: {getDisplayListingStatus(property.listingStatus)}
                                </BodyMedium>
                            </li>
                        )}

                        {property.purpose && (
                            <li>
                                <BodyMedium>
                                    <strong>Purpose of Space</strong>: {getDisplayPurpose(property.purpose)}
                                </BodyMedium>
                            </li>
                        )}

                        {property.density && (
                            <li>
                                <BodyMedium>
                                    <strong>Population Density</strong>: {getDisplayDensity(property.density)}
                                </BodyMedium>
                            </li>
                        )}

                        {property.squareFeet && (
                            <li>
                                <BodyMedium>
                                    <strong>Square Feet</strong>: {property.squareFeet.toLocaleString()} sq ft
                                </BodyMedium>
                            </li>
                        )}

                        {!property.squareFeet && property.size && (
                            <li>
                                <BodyMedium>
                                    <strong>Size of Space</strong>: {getDisplaySize(property.size)}
                                </BodyMedium>
                            </li>
                        )}

                        {property.typeOfCenter && (
                            <li>
                                <BodyMedium>
                                    <strong>Type of Center</strong>: {getDisplayCenter(property.typeOfCenter)}
                                </BodyMedium>
                            </li>
                        )}

                        {property.typeOfFacility && (
                            <li>
                                <BodyMedium>
                                    <strong>Type of Facility</strong>: {getDisplayFacility(property.typeOfFacility)}
                                </BodyMedium>
                            </li>
                        )}
                    </Ul>

                    {property.description && (
                        <Description className="mt-2" dangerouslySetInnerHTML={{ __html: property.description }} />
                    )}

                    {property.url && property.url.length > 0 && (
                        <div className="mt-4">
                            <LabelLarge>URL</LabelLarge>

                            <ul className="list-unstyled">
                                <li>
                                    <LinkSmall href={property.url}>{property.url}</LinkSmall>
                                </li>
                            </ul>
                        </div>
                    )}

                    <div className="d-md-flex mt-4">
                        <PropertyInquiryButton
                            id={manager.recordId}
                            graphID={manager.id}
                            companyID={manager.company?.recordId}
                            businessType={[manager.company?.primaryBusinessType?.name]}
                            companyName={manager.company?.name}
                            designations=""
                            displayLocation={getManagerLocation(property?.manager)}
                            firstName={manager.firstName}
                            fullName={`${manager.firstName} ${manager.lastName}`}
                            lastName={manager.lastName}
                            photoURL={manager.photoUrl}
                            title={manager.title}
                            url={`/member/profile/${manager.recordId}`}
                            messageAttributes={{ source: 'Make an Inquiry' }}
                            onMessage={() => {
                                trackEvent({
                                    click: {
                                        type: 'conversion',
                                        target: 'make_an_inquiry_modal_submit_button',
                                        value: `Messaged ${manager.firstName} ${manager.lastName}`,
                                    },
                                });
                            }}
                        />

                        {property.sitePlan?.url && (
                            <button
                                type="button"
                                className="btn btn-outline-primary ml-md-2 mt-2 mt-md-0"
                                onClick={() => {
                                    const link = document.createElement('a');
                                    link.download = `${property.title} Site Plan`;
                                    link.href = property.sitePlan.url;
                                    link.target = '_blank';
                                    document.body.appendChild(link);
                                    link.click();
                                    document.body.removeChild(link);
                                }}
                            >
                                Download Site Plan
                            </button>
                        )}
                    </div>
                </div>

                <div className="col-lg-6 mt-3 mt-md-0">
                    <HeroCarousel>
                        {property.photos.map(photo => (
                            <HeroCarouselSlide key={photo.id} imageUrl={photo.url} />
                        ))}
                    </HeroCarousel>
                </div>
            </div>

            <div className="mt-5">
                <H4>This property listing is managed by</H4>
                <div className="row mt-3">
                    <div className="col-lg-6 mb-4">
                        <MemberCardRedesign
                            businessType={[manager.company?.primaryBusinessType?.name]}
                            companyName={manager.company?.name}
                            designations=""
                            displayLocation={getManagerLocation(property?.manager)}
                            firstName={manager.firstName}
                            fullName={`${manager.firstName} ${manager.lastName}`}
                            id={manager.recordId}
                            graphID={manager.id}
                            companyID={manager.company?.recordId}
                            lastName={manager.lastName}
                            photoURL={manager.photoUrl}
                            title={manager.title}
                            showMailIcon={false}
                            url={`/member/profile/${manager.recordId}`}
                            onClick={() => handleMemberCardGenericClick()}
                        />
                    </div>
                </div>
            </div>

            {property.country === 'US' && (tradeAreaDetailsUrl || commercialMapsUrl || attributeSiteSelectionUrl) && (
                <div className="mt-5">
                    <H4 className="mb-3">Marketplaces IQ</H4>

                    <MarketplacesContainerStyled>
                        {tradeAreaDetailsUrl && (
                            <MarketplacesButtonStyled as="a" href={tradeAreaDetailsUrl} target="_blank">
                                Trade Area Details
                            </MarketplacesButtonStyled>
                        )}

                        {commercialMapsUrl && (
                            <MarketplacesButtonStyled as="a" href={commercialMapsUrl} target="_blank">
                                Commercial Maps
                            </MarketplacesButtonStyled>
                        )}

                        {attributeSiteSelectionUrl && (
                            <MarketplacesButtonStyled as="a" href={attributeSiteSelectionUrl} target="_blank">
                                Attribute Site Selection
                            </MarketplacesButtonStyled>
                        )}
                    </MarketplacesContainerStyled>

                    {(commercialPropertyReportUrl || commercialTradeAreaReport) && (
                        <MarketplacesContainerStyled
                            className={classNames({ 'mt-0.5rem': isScreenSmDown, 'mt-1rem': isScreenMinMd })}
                        >
                            {commercialPropertyReportUrl && (
                                <MarketplacesButtonStyled as="a" href={commercialPropertyReportUrl} target="_blank">
                                    Commercial Property Report
                                </MarketplacesButtonStyled>
                            )}

                            {commercialTradeAreaReport && (
                                <MarketplacesButtonStyled as="a" href={commercialTradeAreaReport} target="_blank">
                                    Commercial Trade Area Report
                                </MarketplacesButtonStyled>
                            )}
                        </MarketplacesContainerStyled>
                    )}
                </div>
            )}

            <div className="mt-5">
                <H4>Property Location</H4>
                <Mapbox
                    classNames="mt-3"
                    properties={[{ _geoloc: { lng: property.lng, lat: property.lat } }]}
                    latitude={String(property.lat)}
                    longitude={String(property.lng)}
                    zoomLevel={12}
                />
            </div>

            <div className="mt-5">
                <PropertyDetailContacts property={property} onContactAddRemove={() => refetch()} />
            </div>

            <div className="mt-5">
                <H4 className="mb-1.75rem">Affiliated Businesses</H4>

                {property?.businesses.map(business => (
                    <BodyLarge key={business.name} className="mb-1.25rem">
                        {business.name}
                    </BodyLarge>
                ))}
            </div>

            <DividerLightGreyStyled />

            <LabelSmall>
                Property listings are generated from users. ICSC is not responsible for the accuracy of any property
                listing data.
            </LabelSmall>
        </>
    );
};

export default PropertyDetail;
